import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { Modal, Field } from './Extensions';
import { useToast } from '@/hooks/use-toast';

const CallFlows = () => {
    const [items, setItems] = useState([]);
    const [dids, setDids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const [r1, r2] = await Promise.all([api.get('/call-flows'), api.get('/didww/dids')]);
        setItems(r1.data);
        setDids(r2.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...editing };
            if (!payload.nodes) payload.nodes = [];
            if (!payload.working_hours) payload.working_hours = { enabled: false, timezone: 'Europe/Prague', days: [0,1,2,3,4], start: '09:00', end: '17:00', after_hours_node_id: null };
            if (!payload.did_number_id) payload.did_number_id = null;
            await api.post('/call-flows', payload);
            setEditing(null);
            load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const del = async (id) => {
        if (!window.confirm('Smazat call flow?')) return;
        await api.delete(`/call-flows/${id}`);
        load();
    };

    return (
        <>
            <PageHeader
                eyebrow="Routing"
                title="Call Flows"
                description="Pravidla pro směrování příchozích hovorů: uvítání → IVR menu → klapky / ring groups / GSM."
                actions={
                    <button data-testid={TID.cfCreateBtn}
                            onClick={() => setEditing({ name: '', description: '', is_active: true, did_number_id: null, nodes: [], entry_node_id: null, fallback_destination: '' })}
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nový flow
                    </button>
                }
            />

            <Card testId={TID.cfTable}>
                {loading ? <div>Načítání…</div> : items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné call flows. Vytvořte první.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]"
                                style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Název</th>
                                <th className="text-left">DID</th>
                                <th className="text-left">Uzly</th>
                                <th className="text-center">Stav</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((f) => (
                                <tr key={f.id} className="border-t"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2"><Link to={`/call-flows/${f.id}`} className="hover:underline">{f.name}</Link></td>
                                    <td className="font-mono text-xs">
                                        {(dids.find((d) => d.id === f.did_number_id) || {}).number || '—'}
                                    </td>
                                    <td className="font-mono text-xs">{(f.nodes || []).length}</td>
                                    <td className="text-center">
                                        <StatusPill status={f.is_active ? 'success' : 'neutral'}
                                                    label={f.is_active ? 'aktivní' : 'pozastaveno'} />
                                    </td>
                                    <td className="text-right">
                                        <Link to={`/call-flows/${f.id}`}
                                              className="inline-flex p-1 hover:bg-[var(--bg-surface-hover)]"
                                              data-testid={`cf-edit-${f.id}`}>
                                            <PencilSimple size={14} />
                                        </Link>
                                        <button onClick={() => del(f.id)}
                                                className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1"
                                                data-testid={`cf-delete-${f.id}`}>
                                            <Trash size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal title="Nový Call Flow" onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-2 gap-3">
                        <Field label="Název" value={editing.name}
                               onChange={(v) => setEditing({ ...editing, name: v })} required />
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>DID číslo</label>
                            <select value={editing.did_number_id || ''}
                                    onChange={(e) => setEditing({ ...editing, did_number_id: e.target.value || null })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                <option value="">— žádný —</option>
                                {dids.map((d) => <option key={d.id} value={d.id}>{d.number}</option>)}
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Popis</label>
                            <textarea rows={2} value={editing.description || ''}
                                      onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                      className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                      style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <label className="col-span-2 inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!editing.is_active}
                                   onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                            Aktivní
                        </label>
                        <div className="col-span-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            Uzly (uvítání, IVR menu, klapky, ring groups, GSM, voicemail…) přidáte v editoru po vytvoření.
                        </div>
                        <div className="col-span-2 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)}
                                    className="px-3 py-2 text-xs font-bold uppercase border"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid={TID.cfSaveBtn}
                                    className="px-3 py-2 text-xs font-bold uppercase"
                                    style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                                Vytvořit
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default CallFlows;
